import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, resolveMedia } from "@/lib/api";
import { toast } from "sonner";
import { Header, AddBtn, In, IconBtn } from "./shared";
import { RichTextEditor } from "@/components/RichTextEditor";
import { RichTextContent } from "@/components/RichTextContent";
import {
  Basketball, House, PencilSimple, Trash, ArrowUp, ArrowDown, Eye, EyeSlash, X, DotsSixVertical,
  UploadSimple, FilePdf, FileDoc, LinkSimple, Plus, BookOpen, ArrowUpRight,
} from "@phosphor-icons/react";

const CATS = [
  { key: "basketball", label: "Basketball Rules", icon: Basketball },
  { key: "house", label: "House Rules", icon: House },
];

const emptyRule = (category) => ({ category, title: "", subtitle: "", body_html: "", attachments: [], links: [], status: "draft" });

function AttachIcon({ ct }) {
  if ((ct || "").includes("pdf")) return <FilePdf size={16} className="text-[#FF5722]" />;
  if ((ct || "").includes("word") || (ct || "").includes("msword")) return <FileDoc size={16} className="text-[#FF5722]" />;
  return <UploadSimple size={16} className="text-[#FF5722]" />;
}

function RuleEditorModal({ open, initial, category, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [linkForm, setLinkForm] = useState({ label: "", url: "" });
  const fileRef = useRef(null);

  useEffect(() => { setForm(initial); setPreview(false); }, [initial]);
  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/rules/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("attachments", [...(form.attachments || []), { name: res.data.name, url: res.data.url, content_type: res.data.content_type }]);
      toast.success("Attachment added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAttach = (i) => set("attachments", form.attachments.filter((_, idx) => idx !== i));
  const addLink = () => {
    if (!linkForm.url.trim()) { toast.error("Link URL is required"); return; }
    set("links", [...(form.links || []), { label: linkForm.label.trim() || linkForm.url.trim(), url: linkForm.url.trim() }]);
    setLinkForm({ label: "", url: "" });
  };
  const removeLink = (i) => set("links", form.links.filter((_, idx) => idx !== i));

  const doSave = async (status) => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = { ...form, category, status };
    try {
      if (form.id) await api.put(`/rules/${form.id}`, payload);
      else await api.post("/rules", payload);
      toast.success(status === "published" ? "Published" : "Saved as draft");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-2 sm:p-6 bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="bg-[#121212] border border-white/10 w-full max-w-3xl my-4" onClick={(e) => e.stopPropagation()} data-testid="rule-editor-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#121212] z-20">
          <h3 className="font-display text-2xl uppercase">{form.id ? "Edit Rule" : "Add Rule"}</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setPreview((p) => !p)} data-testid="rule-preview-toggle" className={`flex items-center gap-1.5 text-xs uppercase font-bold px-3 py-1.5 border transition-colors ${preview ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-white/70 hover:text-white"}`}>
              <Eye size={15} /> {preview ? "Edit" : "Preview"}
            </button>
            <button onClick={onClose} className="text-white/50 hover:text-white"><X size={22} /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {preview ? (
            <div className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="rule-preview">
              <h2 className="font-display text-3xl uppercase text-[#FF5722] leading-none">{form.title || "Untitled"}</h2>
              {form.subtitle && <p className="text-white/60 text-lg mt-2">{form.subtitle}</p>}
              <div className="w-16 h-1 bg-[#FF5722] my-5" />
              <RichTextContent html={form.body_html} />
              {(form.attachments?.length > 0 || form.links?.length > 0) && (
                <div className="mt-6 pt-5 border-t border-white/10 flex flex-wrap gap-2">
                  {form.attachments.map((a, i) => <span key={a.url || i} className="flex items-center gap-2 border border-white/15 px-3 py-2 text-sm"><AttachIcon ct={a.content_type} />{a.name}</span>)}
                  {form.links.map((l, i) => <span key={l.url || i} className="flex items-center gap-2 border border-white/15 px-3 py-2 text-sm"><ArrowUpRight size={16} className="text-[#FF5722]" />{l.label}</span>)}
                </div>
              )}
            </div>
          ) : (
            <>
              <In label="Section Title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Game Format & Timing" data-testid="rule-title-input" />
              <In label="Subtitle (optional)" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="A short one-liner" data-testid="rule-subtitle-input" />

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Content</label>
                <RichTextEditor value={form.body_html} onChange={(html) => set("body_html", html)} />
              </div>

              {/* Attachments */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Attachments (PDF / Word / Image)</label>
                <div className="space-y-2">
                  {(form.attachments || []).map((a, i) => (
                    <div key={a.url || i} data-testid={`rule-attach-row-${i}`} className="flex items-center gap-2 border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm">
                      <AttachIcon ct={a.content_type} /> <span className="flex-1 truncate">{a.name}</span>
                      <button type="button" onClick={() => removeAttach(i)} data-testid={`rule-attach-remove-${i}`} className="text-white/40 hover:text-[#FF3B30]"><Trash size={16} /></button>
                    </div>
                  ))}
                </div>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={uploadDoc} className="hidden" data-testid="rule-attach-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="rule-attach-btn" className="mt-2 flex items-center gap-2 text-xs uppercase font-bold px-3 py-2 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 transition-colors disabled:opacity-50">
                  <UploadSimple size={16} /> {uploading ? "Uploading…" : "Upload Document"}
                </button>
              </div>

              {/* External links */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">External Links (documents / videos)</label>
                <div className="space-y-2">
                  {(form.links || []).map((l, i) => (
                    <div key={l.url || i} data-testid={`rule-link-row-${i}`} className="flex items-center gap-2 border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm">
                      <LinkSimple size={16} className="text-[#FF5722]" /> <span className="flex-1 truncate">{l.label} — <span className="text-white/40">{l.url}</span></span>
                      <button type="button" onClick={() => removeLink(i)} data-testid={`rule-link-remove-${i}`} className="text-white/40 hover:text-[#FF3B30]"><Trash size={16} /></button>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                  <input value={linkForm.label} onChange={(e) => setLinkForm((f) => ({ ...f, label: e.target.value }))} placeholder="Label" data-testid="rule-link-label" className="bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] sm:w-40" />
                  <input value={linkForm.url} onChange={(e) => setLinkForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://…" data-testid="rule-link-url" className="bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] flex-1" />
                  <button type="button" onClick={addLink} data-testid="rule-link-add" className="flex items-center gap-1 text-xs uppercase font-bold px-3 py-2 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70"><Plus size={16} /> Add</button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 px-6 py-4 border-t border-white/10 sticky bottom-0 bg-[#121212]">
          <button onClick={onClose} className="px-4 py-2.5 text-sm uppercase font-bold text-white/60 hover:text-white">Cancel</button>
          <button onClick={() => doSave("draft")} disabled={saving} data-testid="rule-save-draft" className="px-5 py-2.5 text-sm uppercase font-bold border border-white/20 text-white/80 hover:border-white/50 disabled:opacity-50 transition-colors">Save as Draft</button>
          <button onClick={() => doSave("published")} disabled={saving} data-testid="rule-publish" className="px-5 py-2.5 text-sm uppercase font-bold bg-[#FF5722] hover:bg-[#FF7043] text-black disabled:opacity-50 transition-colors">Publish</button>
        </div>
      </div>
    </div>
  );
}

export default function RulesTab() {
  const [cat, setCat] = useState("basketball");
  const [rules, setRules] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const dragIdx = useRef(null);

  const load = (c = cat) => api.get(`/admin/rules?category=${c}`).then((r) => setRules(r.data || [])).catch(() => {});
  useEffect(() => { load(cat); }, [cat]); // eslint-disable-line

  const openAdd = () => { setEditing(emptyRule(cat)); setModalOpen(true); };
  const openEdit = (r) => { setEditing({ ...r }); setModalOpen(true); };
  const onSaved = () => { setModalOpen(false); load(); };

  const remove = async (r) => {
    if (!window.confirm(`Delete rule "${r.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/rules/${r.id}`); toast.success("Rule deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const togglePublish = async (r) => {
    try {
      await api.put(`/rules/${r.id}`, { ...r, status: r.status === "published" ? "draft" : "published" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const persistOrder = async (arr) => {
    setRules(arr);
    try { await api.post("/rules/reorder", { category: cat, ids: arr.map((r) => r.id) }); }
    catch { toast.error("Could not reorder"); load(); }
  };

  const move = (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= rules.length) return;
    const arr = [...rules];
    [arr[index], arr[next]] = [arr[next], arr[index]];
    persistOrder(arr);
  };

  const onDrop = (index) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from == null || from === index) return;
    const arr = [...rules];
    const [moved] = arr.splice(from, 1);
    arr.splice(index, 0, moved);
    persistOrder(arr);
  };

  return (
    <div data-testid="rules-tab">
      <Header title="IEFSC Rules" action={<AddBtn onClick={openAdd} label="Add Rule" testid="add-rule-btn" />} />
      <p className="text-white/40 text-sm mb-6 -mt-4">Manage Basketball and House Rules with a full rich-text editor. Drag to reorder, save drafts, and publish instantly to the public IEFSC Rules page.</p>

      <div className="flex gap-2 flex-wrap mb-6">
        {CATS.map((c) => (
          <button key={c.key} data-testid={`rules-admin-tab-${c.key}`} onClick={() => setCat(c.key)}
            className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wide flex items-center gap-2 transition-all ${cat === c.key ? "bg-[#FF5722] text-black" : "border border-white/15 text-white/70 hover:text-white"}`}>
            <c.icon size={16} weight={cat === c.key ? "fill" : "regular"} /> {c.label}
          </button>
        ))}
      </div>

      {rules.length === 0 ? (
        <div className="border border-dashed border-white/15 py-16 text-center" data-testid="rules-admin-empty">
          <BookOpen size={40} className="text-white/30 mx-auto mb-3" />
          <p className="text-white/50">No rules yet. Add your first rule for this section.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((r, i) => {
            const published = r.status === "published";
            return (
              <div key={r.id} data-testid={`rule-row-${i}`}
                draggable
                onDragStart={() => { dragIdx.current = i; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(i)}
                className="flex items-center gap-3 border border-white/10 bg-[#121212] p-4">
                <span className="text-white/25 cursor-grab active:cursor-grabbing" title="Drag to reorder"><DotsSixVertical size={20} /></span>
                <div className="flex flex-col">
                  <IconBtn onClick={() => move(i, -1)} testid={`rule-up-${i}`}><ArrowUp size={16} /></IconBtn>
                  <IconBtn onClick={() => move(i, 1)} testid={`rule-down-${i}`}><ArrowDown size={16} /></IconBtn>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold uppercase truncate" data-testid={`rule-title-${i}`}>{r.title}</p>
                    <span data-testid={`rule-status-${i}`} className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 ${published ? "bg-[#25a244]/20 text-[#4ade80]" : "bg-white/10 text-white/50"}`}>{published ? "Published" : "Draft"}</span>
                  </div>
                  {r.subtitle && <p className="text-xs text-white/40 mt-0.5 truncate">{r.subtitle}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <IconBtn onClick={() => togglePublish(r)} testid={`rule-toggle-${i}`}>{published ? <EyeSlash size={18} /> : <Eye size={18} />}</IconBtn>
                  <IconBtn onClick={() => openEdit(r)} testid={`rule-edit-${i}`}><PencilSimple size={18} /></IconBtn>
                  <IconBtn onClick={() => remove(r)} danger testid={`rule-delete-${i}`}><Trash size={18} /></IconBtn>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <RuleEditorModal open={modalOpen} initial={editing || emptyRule(cat)} category={cat} onClose={() => setModalOpen(false)} onSaved={onSaved} />
    </div>
  );
}
